import { FC, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  ArrowLeft,
  PencilSimple,
  Clock,
  Stack,
  UsersThree,
  CalendarBlank,
  Lightning,
  ChartBar,
} from '@phosphor-icons/react';
import { coachSessionsEndpoints, IBlockBase } from '../../endpoints/coachSessions';
import { OBJECTIVE_LABELS, OBJECTIVE_COLORS } from '../CoachSessionWeekView/useCoachSessionWeekView';
import { BLOCK_TYPE_CONFIG } from '../CoachSessionDetail/blocks/BlockBase';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import {
  BLOCK_TYPE_LABELS,
  ATHLETE_STATUS_CONFIG,
  ATHLETE_STATUS_ORDER,
  LOAD_PROFILE_LABELS,
} from './coachSessionView.constants';
import styles from './CoachSessionView.module.scss';

/**
 * VISTA SESSIONE CAMPO — sola lettura (bug a48bb7 + redesign UX full-width).
 * Layout a 2 colonne (timeline programma + pannello roster/metriche/note),
 * header sticky full-width con KPI. Il tasto MODIFICA porta all'editor.
 */

/** Riga di sintesi essenziale di un blocco (campo · densità · serie · gruppi · atleti). */
function blockSynthesis(b: IBlockBase): string {
  const parts: string[] = [];
  const anyB = b as any;
  if (b.fieldLengthM && b.fieldWidthM) {
    parts.push(`campo ${b.fieldLengthM}×${b.fieldWidthM} m`);
    const players = b.involvedAthleteIds?.length;
    if (players && players > 0) {
      parts.push(`${Math.round(((b.fieldLengthM * b.fieldWidthM) / players) * 10) / 10} m²/gioc`);
    }
  }
  if (anyB.gymSchemaSnapshot?.title) {
    const s = anyB.gymSchemaSnapshot;
    parts.push([s.title, s.exerciseCount ? `${s.exerciseCount} es.` : null].filter(Boolean).join(' · '));
  }
  if (Array.isArray(b.physicalWorkRefs) && b.physicalWorkRefs.length) {
    parts.push(`${b.physicalWorkRefs.length} sched${b.physicalWorkRefs.length === 1 ? 'a' : 'e'}`);
  }
  if (anyB.series) parts.push(`${anyB.series}×${anyB.reps ?? '—'}`);
  if (anyB.load) parts.push(String(anyB.load));
  if (anyB.restSeconds) parts.push(`rec ${anyB.restSeconds}s`);
  if (Array.isArray(b.tracks) && b.tracks.length) parts.push(`${b.tracks.length} gruppi`);
  if (Array.isArray(b.involvedAthleteIds) && b.involvedAthleteIds.length)
    parts.push(`${b.involvedAthleteIds.length} atleti`);
  if (b.blockType === 'rotation' && anyB.numberOfRotations) parts.push(`${anyB.numberOfRotations} rotazioni`);
  return parts.join(' · ');
}

const blockName = (b: IBlockBase): string =>
  (b as any).label ||
  b.exerciseName ||
  (b as any).customExerciseName ||
  (b as any).breakLabel ||
  BLOCK_TYPE_LABELS[b.blockType] ||
  b.blockType;

const CoachSessionViewPage: FC = () => {
  const { teamId, sessionId } = useParams<{ teamId: string; sessionId: string }>();
  const navigate = useNavigate();

  const { data: session, isLoading } = useQuery({
    queryKey: ['coachSession', sessionId],
    queryFn: () => coachSessionsEndpoints.get(sessionId ?? '').then((r) => r.data),
    enabled: !!sessionId,
  });

  const blocks = useMemo(
    () => [...(session?.blocks ?? [])].sort((a, b) => a.order - b.order),
    [session],
  );

  const athletesByStatus = useMemo(() => {
    const groups: Record<string, NonNullable<typeof session>['athletes']> = {};
    for (const a of session?.athletes ?? []) {
      (groups[a.status] ??= []).push(a);
    }
    return groups;
  }, [session]);

  const staffNotes = useMemo(
    () =>
      (session?.blocks ?? [])
        .map((b) => ({ name: blockName(b), note: (b as any).staffNotes as string | undefined }))
        .filter((x) => x.note && x.note.trim()),
    [session],
  );

  if (isLoading) {
    return (
      <div className={styles.loadingWrap}>
        <LeopartLoader />
      </div>
    );
  }
  if (!session) {
    return <div className={styles.loadingWrap}>Sessione non trovata.</div>;
  }

  const m = session.metrics;
  const totalDuration =
    m?.totalDurationMinutes ?? blocks.reduce((acc, b) => acc + (b.durationMinutes ?? 0), 0);
  const athleteCount = session.athletes?.length ?? 0;
  const objLabel = OBJECTIVE_LABELS[session.objective ?? ''] ?? session.objective ?? '';
  const objColor = OBJECTIVE_COLORS[session.objective ?? ''] ?? '#b5b5b5';
  const dateLabel = session.scheduledDate
    ? dayjs(session.scheduledDate).locale('it').format('dddd D MMMM YYYY')
    : '';
  const statusLabel =
    session.status === 'published' ? 'Pubblicata' : session.status === 'executed' ? 'Svolta' : 'Bozza';

  const goEdit = () => navigate(`/dashboard/teams/${teamId}/sessions/${sessionId}/edit`);
  const goBack = () => navigate(-1);

  const kpis: { icon: JSX.Element; value: string; label: string }[] = [
    { icon: <Clock size={18} weight="bold" />, value: `${totalDuration}'`, label: 'Durata' },
    { icon: <Stack size={18} weight="bold" />, value: String(blocks.length), label: 'Blocchi' },
    { icon: <UsersThree size={18} weight="bold" />, value: String(athleteCount), label: 'Atleti' },
  ];
  if (m?.ewtMinutes != null)
    kpis.push({
      icon: <Lightning size={18} weight="bold" />,
      value: `${m.ewtMinutes}'${m.ewtPct != null ? ` · ${Math.round(m.ewtPct)}%` : ''}`,
      label: 'EWT',
    });
  if (m?.totalCE != null)
    kpis.push({ icon: <ChartBar size={18} weight="bold" />, value: String(Math.round(m.totalCE)), label: 'Carico est.' });

  return (
    <div className={styles.pageWrapper}>
      {/* Header sticky full-width */}
      <div className={styles.stickyHeader}>
        <div className={styles.headerTop}>
          <button type="button" className={styles.backBtn} onClick={goBack} aria-label="Indietro">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className={styles.headerInfo}>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{session.title || 'Sessione'}</h1>
              <span className={styles.readBadge}>Sola lettura</span>
              <span className={styles.statusPill} data-status={session.status}>{statusLabel}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaItem}>
                <CalendarBlank size={14} weight="bold" />
                {dateLabel}
              </span>
              {session.startTime && (
                <span className={styles.metaItem}>
                  <Clock size={14} weight="bold" />
                  {session.startTime}
                </span>
              )}
              {objLabel && (
                <span className={styles.objChip} style={{ background: objColor }}>
                  {objLabel}
                </span>
              )}
            </div>
          </div>
          <button type="button" className={styles.editBtn} onClick={goEdit}>
            <PencilSimple size={16} weight="bold" />
            Modifica
          </button>
        </div>

        <div className={styles.kpiStrip}>
          {kpis.map((k) => (
            <div key={k.label} className={styles.kpi}>
              <div className={styles.kpiIcon}>{k.icon}</div>
              <div className={styles.kpiText}>
                <span className={styles.kpiValue}>{k.value}</span>
                <span className={styles.kpiLabel}>{k.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Corpo 2 colonne */}
      <div className={styles.bodyGrid}>
        {/* Colonna principale: timeline programma */}
        <div className={styles.timelineCol}>
          <div className={styles.sectionLabel}>Programma</div>
          {blocks.length === 0 ? (
            <div className={styles.emptyBlocks}>Nessun blocco in questa sessione.</div>
          ) : (
            <div className={styles.blockList}>
              {blocks.map((b, idx) => {
                const cfg = (BLOCK_TYPE_CONFIG as any)[b.blockType];
                const accent = cfg?.color ?? '#94a3b8';
                const Icon = cfg?.Icon;
                const synth = blockSynthesis(b);
                const notes = (b as any).notes as string | undefined;
                return (
                  <div
                    key={b._id ?? idx}
                    className={styles.blockRow}
                    style={{ ['--block-accent' as any]: accent }}
                  >
                    <div className={styles.blockAccent} />
                    <div className={styles.blockNum}>{idx + 1}</div>
                    <div className={styles.blockMinutes}>{b.durationMinutes ? `${b.durationMinutes}'` : '—'}</div>
                    <div className={styles.blockMain}>
                      <div className={styles.blockTitle}>
                        {Icon && (
                          <span className={styles.blockIcon}>
                            <Icon size={15} color={accent} />
                          </span>
                        )}
                        {blockName(b)}
                        <span className={styles.blockTypeBadge}>
                          {BLOCK_TYPE_LABELS[b.blockType] ?? b.blockType}
                        </span>
                      </div>
                      {synth && <div className={styles.blockSynth}>{synth}</div>}
                      {notes && notes.trim() && <div className={styles.blockNotes}>{notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Colonna laterale: roster + metriche + note staff */}
        <aside className={styles.sidePanel}>
          <section className={styles.panelCard}>
            <div className={styles.panelTitle}>Roster · {athleteCount} atleti</div>
            {athleteCount === 0 ? (
              <div className={styles.panelEmpty}>Nessun atleta assegnato.</div>
            ) : (
              ATHLETE_STATUS_ORDER.filter((s) => athletesByStatus[s]?.length).map((s) => {
                const cfg = ATHLETE_STATUS_CONFIG[s];
                const list = athletesByStatus[s]!;
                return (
                  <div key={s} className={styles.statusGroup}>
                    <div className={styles.statusHead}>
                      <span className={styles.statusDot} style={{ background: cfg.color }} />
                      {cfg.label}
                      <span className={styles.statusCount}>{list.length}</span>
                    </div>
                    {list.map((a) => (
                      <div key={a.athleteId} className={styles.athleteRow}>
                        <span className={styles.athleteAvatar} style={{ background: cfg.bg, color: cfg.color }}>
                          {(a.firstName?.[0] ?? '') + (a.lastName?.[0] ?? '') || '?'}
                        </span>
                        <span className={styles.athleteName}>
                          {`${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.athleteId.slice(-6)}
                        </span>
                        {a.recoveryIndex != null && (
                          <span className={styles.athleteRi}>{Math.round(a.recoveryIndex)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </section>

          {m && (m.avgIPS != null || m.avgCLS != null || m.totalCE != null || m.loadProfile) && (
            <section className={styles.panelCard}>
              <div className={styles.panelTitle}>Metriche sessione</div>
              <div className={styles.metricsGrid}>
                {m.avgIPS != null && (
                  <div className={styles.metric}><span className={styles.metricVal}>{m.avgIPS.toFixed(2)}</span><span className={styles.metricLab}>IPS medio</span></div>
                )}
                {m.avgCLS != null && (
                  <div className={styles.metric}><span className={styles.metricVal}>{m.avgCLS.toFixed(2)}</span><span className={styles.metricLab}>CLS medio</span></div>
                )}
                {m.totalCE != null && (
                  <div className={styles.metric}><span className={styles.metricVal}>{Math.round(m.totalCE)}</span><span className={styles.metricLab}>Carico est.</span></div>
                )}
                {m.estimatedAU != null && (
                  <div className={styles.metric}><span className={styles.metricVal}>{Math.round(m.estimatedAU)}</span><span className={styles.metricLab}>AU stimate</span></div>
                )}
              </div>
              {m.loadProfile && (
                <div className={styles.profileChip}>{LOAD_PROFILE_LABELS[m.loadProfile] ?? m.loadProfile}</div>
              )}
            </section>
          )}

          {staffNotes.length > 0 && (
            <section className={styles.panelCard}>
              <div className={styles.panelTitle}>Note staff</div>
              {staffNotes.map((n, i) => (
                <div key={i} className={styles.staffNote}>
                  <span className={styles.staffNoteFrom}>{n.name}</span>
                  <span className={styles.staffNoteText}>{n.note}</span>
                </div>
              ))}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CoachSessionViewPage;
